import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { AlertTriangle, FileCheck2, Loader2 } from "lucide-react";

export const ContractAcceptanceModal = ({ contract, onAccepted, onReload }) => {
  const { logout } = useAuth();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setChecked(false); setError(""); },
    [contract.sequence, contract.renewal_sequence, contract.preview_hash, contract.signing_date]);
  useEffect(() => {
    if (!contract.signing_date) return;
    const timer = setInterval(() => {
      const today = new Intl.DateTimeFormat("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome",
      }).format(new Date());
      if (today !== contract.signing_date) onReload();
    }, 60000);
    return () => clearInterval(timer);
  }, [contract.signing_date, onReload]);

  const accept = async () => {
    if (!checked || saving || contract.missing_fields?.length) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/contract/accept", { sequence: contract.sequence, preview_date: contract.signing_date,
        renewal_sequence: contract.renewal_sequence, preview_hash: contract.preview_hash },
      { withCredentials: true });
      await onAccepted(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : detail?.message || "Accettazione non riuscita. Riprova.");
      if (err.response?.status === 409) await onReload();
    } finally { setSaving(false); }
  };

  return <Dialog open onOpenChange={() => {}} modal>
    <DialogContent showCloseButton={false} onEscapeKeyDown={(event) => event.preventDefault()}
      onPointerDownOutside={(event) => event.preventDefault()}
      className="left-0 top-0 translate-x-0 translate-y-0 w-screen max-w-none h-[100dvh] sm:rounded-none bg-[#F7F8F5] border-0 shadow-none p-0 flex flex-col gap-0"
      data-testid="contract-acceptance-modal">
      <DialogHeader className="shrink-0 border-b border-[#DDE2DE] bg-white px-5 sm:px-10 py-5 flex-row items-center justify-between gap-4 text-left">
        <div>
          <span className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#4A6B53]">
            <FileCheck2 className="w-4 h-4" /> Cosmely · v{contract.version}
          </span>
          <DialogTitle className="font-serif text-2xl sm:text-3xl mt-1">{contract.renewal_sequence ? `Rinnovo n. ${contract.renewal_sequence} · Il tuo contratto` : "Il tuo contratto"}</DialogTitle>
          <DialogDescription className="text-sm text-[#707070] mt-1">Leggi il documento prima di accedere all'area riservata.</DialogDescription>
        </div>
        <Button variant="outline" onClick={logout} data-testid="contract-logout-button">Esci</Button>
      </DialogHeader>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-7 sm:px-10 sm:py-10" data-testid="contract-scroll-region">
        <article className="prose-rt max-w-3xl mx-auto text-sm sm:text-base leading-relaxed break-words"
          dangerouslySetInnerHTML={{ __html: contract.rendered_html || "" }} data-testid="contract-rendered-text" />
      </div>
      <div className="shrink-0 border-t border-[#DDE2DE] bg-white px-5 sm:px-10 py-5" data-testid="contract-acceptance-footer">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <div className="min-w-0">
            {contract.missing_fields?.length > 0 ? <p role="alert" className="flex items-start gap-2 text-sm text-[#8C3A3A]" data-testid="contract-missing-fields">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              Dati contrattuali incompleti ({contract.missing_fields.join(", ")}). Contatta Cosmely per completarli.
            </p> : <label className="flex items-start gap-3 text-sm font-medium cursor-pointer" htmlFor="contract-accept-checkbox">
              <Checkbox id="contract-accept-checkbox" checked={checked} onCheckedChange={(value) => setChecked(value === true)}
                        className="contract-check h-5 w-5 border-[#60766A] bg-white data-[state=checked]:border-[#315748] focus-visible:ring-[#315748]"
                        style={{ backgroundColor: checked ? "#000000" : "#FFFFFF", color: checked ? "#FFFFFF" : "#000000" }}
                        data-testid="contract-accept-checkbox" />
              <span>Ho letto e accetto il contratto</span>
            </label>}
            {error && <p role="alert" className="text-sm text-[#8C3A3A] mt-2" data-testid="contract-accept-error">{error}</p>}
          </div>
          <Button onClick={accept} disabled={!checked || saving || !!contract.missing_fields?.length}
                  className="shrink-0 bg-[#315748] hover:bg-[#244436] text-white" data-testid="contract-accept-button">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Accetta e continua
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>;
};